import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Download, FileDown, RefreshCw, List, CalendarDays, ChevronDown, ChevronUp, Store, Calculator } from "lucide-react";
import { toast } from "sonner";
import LojaSelector from "@/components/LojaSelector";
import LoadingSpinner from "@/components/LoadingSpinner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { logError } from "@/lib/logger";
import { useLojas } from "@/hooks/useLojas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTO_REFRESH_MS = 60000;

const COR_FORMA_PGTO = {
  PIX: "bg-emerald-100 text-emerald-700",
  "Crédito": "bg-blue-100 text-blue-700",
  "Débito": "bg-purple-100 text-purple-700",
  Dinheiro: "bg-green-100 text-green-700",
  Pendente: "bg-amber-100 text-amber-700",
};

const FORMAS_PAGAMENTO = ["Todas", "Crédito", "Débito", "PIX", "Dinheiro", "Crediário", "Pendente"];

const ConferenciaCaixa = () => {
  const LOJAS_GATE = useLojas();
  // Modo: "diario" ou "mensal"
  const [modo, setModo] = useState("diario");
  
  // Estados para modo diário
  const [dataConferencia, setDataConferencia] = useState("");
  const [lojaSelecionada, setLojaSelecionada] = useState("Todas as lojas");
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const intervalRef = useRef(null);
  
  // Estados para modo mensal
  const [mesSelecionado, setMesSelecionado] = useState(() => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  });
  const [formaPagamentoFiltro, setFormaPagamentoFiltro] = useState("Todas");
  const [dadosMensais, setDadosMensais] = useState(null);
  const [loadingMensal, setLoadingMensal] = useState(false);
  const [diasExpandidos, setDiasExpandidos] = useState({});
  const [lojaGate, setLojaGate] = useState(false);
  const [alertasCaixa, setAlertasCaixa] = useState([]);
  const [senhaBaixa, setSenhaBaixa] = useState("");

  const carregarAlertasCaixa = useCallback(async () => {
    try {
      const params = lojaSelecionada !== "Todas as lojas" ? { loja: lojaSelecionada } : {};
      const res = await axios.get(`${API}/alertas-caixa`, { params });
      setAlertasCaixa(res.data.alertas || []);
    } catch (e) {
      logError("Erro ao carregar alertas de caixa:", e);
    }
  }, [lojaSelecionada]);

  useEffect(() => { if (lojaGate) carregarAlertasCaixa(); }, [lojaGate, carregarAlertasCaixa]);

  const darBaixaAlerta = async (id) => {
    if (!senhaBaixa) { toast.error("Digite a senha para dar baixa"); return; }
    try {
      await axios.post(`${API}/alertas-caixa/${id}/baixar`, null, { params: { senha: senhaBaixa } });
      setAlertasCaixa((l) => l.filter((a) => a.id !== id));
      toast.success("Baixa registrada");
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Senha incorreta" : "Erro ao dar baixa");
    }
  };

  // Na montagem, busca a data da ultima venda para usar como padrao
  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/ultima-venda`);
        setDataConferencia(res.data.data);
      } catch {
        setDataConferencia(new Date(Date.now() - 3 * 3600 * 1000).toISOString().split("T")[0]);
      }
    };
    init();
  }, []);

  const carregarDados = useCallback(async (silencioso = false) => {
    if (!dataConferencia) return;
    try {
      if (!silencioso) setLoading(true);
      const res = await axios.get(`${API}/dashboard/fechamento-caixa`, {
        params: { data: dataConferencia, loja: lojaSelecionada },
      });
      setDados(res.data);
      setUltimaAtualizacao(new Date());
    } catch (error) {
      if (!silencioso) toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [dataConferencia, lojaSelecionada]);

  useEffect(() => {
    if (dataConferencia) carregarDados();
  }, [dataConferencia, lojaSelecionada, carregarDados]);

  // Auto-refresh quando data = hoje
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const hoje = new Date(Date.now() - 3 * 3600 * 1000).toISOString().split("T")[0];
    if (dataConferencia === hoje && modo === "diario") {
      intervalRef.current = setInterval(() => carregarDados(true), AUTO_REFRESH_MS);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [dataConferencia, carregarDados, modo]);

  // Carregar dados mensais
  const carregarDadosMensais = useCallback(async () => {
    try {
      setLoadingMensal(true);
      const [ano, mes] = mesSelecionado.split("-");
      const primeiroDia = `${mesSelecionado}-01`;
      const ultimoDia = new Date(parseInt(ano), parseInt(mes), 0).getDate();
      const ultimoDiaStr = `${mesSelecionado}-${String(ultimoDia).padStart(2, '0')}`;
      
      const res = await axios.get(`${API}/dashboard/conferencia-mensal`, {
        params: { 
          data_inicio: primeiroDia, 
          data_fim: ultimoDiaStr, 
          loja: lojaSelecionada,
          forma_pagamento: formaPagamentoFiltro !== "Todas" ? formaPagamentoFiltro : null
        },
      });
      setDadosMensais(res.data);
    } catch (error) {
      toast.error("Erro ao carregar dados mensais");
    } finally {
      setLoadingMensal(false);
    }
  }, [mesSelecionado, lojaSelecionada, formaPagamentoFiltro]);

  useEffect(() => {
    if (modo === "mensal") {
      carregarDadosMensais();
    }
  }, [modo, mesSelecionado, lojaSelecionada, formaPagamentoFiltro, carregarDadosMensais]);

  const toggleDiaExpandido = (dia) => {
    setDiasExpandidos(prev => ({ ...prev, [dia]: !prev[dia] }));
  };

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
  const hoje = new Date(Date.now() - 3 * 3600 * 1000).toISOString().split("T")[0];
  const isHoje = dataConferencia === hoje;

  const exportarCSV = () => {
    if (!dados) return;
    const s = dados.stats;
    const rows = [
      ["FECHAMENTO DE CAIXA"],
      [`Data: ${new Date(dados.data + "T12:00:00").toLocaleDateString("pt-BR")}`],
      [`Loja: ${dados.loja}`],
      [""],
      ["Total", fmt(s.total_vendas)],
      ["Vendas", s.total_receitas],
      ["Ticket Medio", fmt(s.media_venda)],
      [""],
      ["FORMA PAGAMENTO", "QTD", "VALOR"],
      ...(s.vendas_por_forma_pagamento || []).map((i) => [i.forma_pagamento, i.quantidade, i.valor]),
      [""],
      ["HORA", "OS", "CLIENTE", "PAGAMENTO", "VALOR"],
      ...dados.detalhes.map((d) => [d.hora, d.os, d.cliente, d.forma_pagamento, d.valor]),
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `fechamento_${dados.data}.csv`;
    a.click();
    toast.success("CSV exportado!");
  };

  const exportarPDF = () => {
    if (!dados) return;
    const s = dados.stats;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Fechamento de Caixa", 14, 20);
    doc.setFontSize(10);
    doc.text(`Data: ${new Date(dados.data + "T12:00:00").toLocaleDateString("pt-BR")}  |  Loja: ${dados.loja}`, 14, 28);

    autoTable(doc, {
      startY: 34,
      head: [["Indicador", "Valor"]],
      body: [
        ["Total", fmt(s.total_vendas)],
        ["Vendas", String(s.total_receitas)],
        ["Ticket Medio", fmt(s.media_venda)],
      ],
      theme: "grid", headStyles: { fillColor: [15, 23, 42] },
    });

    if (s.vendas_por_forma_pagamento?.length > 0) {
      const y = doc.lastAutoTable?.finalY || 70;
      doc.text("Por Forma de Pagamento", 14, y + 10);
      autoTable(doc, {
        startY: y + 14,
        head: [["Forma", "Qtd", "Valor"]],
        body: s.vendas_por_forma_pagamento.map((i) => [i.forma_pagamento, String(i.quantidade), fmt(i.valor)]),
        theme: "grid", headStyles: { fillColor: [15, 23, 42] },
      });
    }

    if (dados.detalhes?.length > 0) {
      const y2 = doc.lastAutoTable?.finalY || 100;
      doc.text("Detalhamento", 14, y2 + 10);
      autoTable(doc, {
        startY: y2 + 14,
        head: [["Hora", "OS", "Cliente", "Pagamento", "Valor"]],
        body: dados.detalhes.map((d) => [d.hora, d.os || "-", d.cliente || "-", d.forma_pagamento || "-", fmt(d.valor)]),
        theme: "grid", headStyles: { fillColor: [15, 23, 42] },
      });
    }

    doc.save(`fechamento_${dados.data}.pdf`);
    toast.success("PDF exportado!");
  };

  if (!lojaGate) {
    const opcoes = [
      { value: "Todas as lojas", label: "Gerencial (todas as lojas)", gerencial: true },
      ...LOJAS_GATE.map((l) => ({ value: l, label: l })),
    ];
    const escolher = (v) => { setLojaSelecionada(v); setLojaGate(true); };
    return (
      <div className="max-w-2xl mx-auto" data-testid="fechamento-loja-gate">
        <div className="text-center mb-8 mt-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-4"><Calculator className="w-7 h-7" /></div>
          <h1 className="text-3xl font-bold text-slate-900">Fechamento de Caixa — Selecione a loja</h1>
          <p className="text-sm text-slate-500 mt-1">Escolha uma loja ou entre no modo gerencial</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {opcoes.map((o) => (
            <button key={o.value} onClick={() => escolher(o.value)}
              data-testid={`fechamento-gate-${o.value.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left hover:shadow-md ${
                o.gerencial ? "border-slate-900 bg-slate-900 text-white sm:col-span-2 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
              }`}>
              <Store className={`w-5 h-5 shrink-0 ${o.gerencial ? "text-white" : "text-slate-500"}`} />
              <span className="font-semibold">{o.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="conferencia-caixa-container">
      {alertasCaixa.length > 0 && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="alertas-caixa-panel">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <h2 className="text-base font-bold text-amber-900">Vendas em dinheiro pendentes de baixa ({alertasCaixa.length})</h2>
            <input type="password" value={senhaBaixa} onChange={(e) => setSenhaBaixa(e.target.value)} placeholder="Senha p/ baixa" className="ml-auto text-xs border border-amber-300 rounded px-2 py-1 bg-white" data-testid="alerta-senha-baixa" />
          </div>
          <div className="space-y-2">
            {alertasCaixa.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-amber-200 p-2.5" data-testid={`alerta-caixa-${a.id}`}>
                <div className="text-sm min-w-0">
                  <span className="font-bold text-slate-900">R$ {Number(a.valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
                  <span className="text-slate-600"> · {a.loja || "-"} · {a.nome_cliente || "-"}{a.os ? ` · OS ${a.os}` : ""}</span>
                  <span className="block text-[11px] text-slate-400">{a.data_criacao ? new Date(a.data_criacao).toLocaleString("pt-BR") : ""}</span>
                </div>
                <Button size="sm" onClick={() => darBaixaAlerta(a.id)} data-testid={`alerta-baixa-${a.id}`} className="bg-slate-900 text-white hover:bg-slate-800 shrink-0">Dar baixa</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Fechamento de Caixa</h1>
          <p className="text-sm text-slate-500 mt-1">Conferencia diaria e mensal de vendas{lojaSelecionada !== "Todas as lojas" ? ` · ${lojaSelecionada}` : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setLojaGate(false)} data-testid="fechamento-trocar-loja">
            <Store className="w-3.5 h-3.5 mr-1.5" /> Trocar loja
          </Button>
          {modo === "diario" && (
            <>
              <Button variant="outline" size="sm" onClick={exportarCSV} data-testid="btn-exportar-csv">
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={exportarPDF} data-testid="btn-exportar-pdf">
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Toggle Modo */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
        <button
          onClick={() => setModo("diario")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            modo === "diario" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"
          }`}
          data-testid="btn-modo-diario"
        >
          <Calendar className="w-4 h-4" /> Diário
        </button>
        <button
          onClick={() => setModo("mensal")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            modo === "mensal" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"
          }`}
          data-testid="btn-modo-mensal"
        >
          <CalendarDays className="w-4 h-4" /> Conferência Mensal
        </button>
      </div>

      {modo === "diario" ? (
        /* ========== MODO DIÁRIO ========== */
        <>
          {/* Filtros */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs font-medium">Data</Label>
              <Input type="date" value={dataConferencia} onChange={(e) => setDataConferencia(e.target.value)}
                className="w-44" data-testid="input-data-conferencia" />
            </div>
            <LojaSelector lojaSelecionada={lojaSelecionada} onLojaChange={setLojaSelecionada} />
            <Button onClick={() => carregarDados()} size="sm" data-testid="btn-atualizar">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Atualizar
            </Button>
            {isHoje && ultimaAtualizacao && (
              <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 flex items-center gap-1.5"
                data-testid="auto-refresh-indicator">
                <RefreshCw className="w-3 h-3 animate-spin" style={{ animationDuration: "3s" }} />
                Auto-refresh ativo - {ultimaAtualizacao.toLocaleTimeString("pt-BR")}
              </span>
            )}
          </div>

          {loading ? (
            <LoadingSpinner message="Carregando fechamento..." />
          ) : dados ? (
            <>
              {/* Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg" data-testid="card-total-dia">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-emerald-100 text-sm">Total do Dia</p>
                    <p className="text-3xl font-bold mt-1">{fmt(dados.stats.total_vendas)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-slate-500 text-sm">Vendas</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{dados.stats.total_receitas}</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-slate-500 text-sm">Ticket Medio</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{fmt(dados.stats.media_venda)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Formas de Pagamento */}
              {dados.stats.vendas_por_forma_pagamento?.length > 0 && (
                <Card className="bg-white border shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-slate-900">Por Forma de Pagamento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dados.stats.vendas_por_forma_pagamento.map((item) => (
                        <div key={item.forma_pagamento} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{item.forma_pagamento}</p>
                            <p className="text-xs text-slate-500">{item.quantidade} vendas</p>
                          </div>
                          <p className="font-bold text-slate-900">{fmt(item.valor)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tabela de Detalhes */}
              <Card className="bg-white border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Detalhamento</CardTitle>
                  {dados.stats?.quitacoes_do_dia > 0 && (
                    <p className="text-xs text-purple-600 mt-1">
                      Inclui {dados.stats.quitacoes_do_dia} quitação(ões) de vendas anteriores: {fmt(dados.stats.valor_quitacoes)}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {dados.detalhes?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="tabela-detalhes">
                        <thead>
                          <tr className="border-b border-slate-200 text-left">
                            <th className="p-2.5 font-semibold text-slate-600">Hora</th>
                            <th className="p-2.5 font-semibold text-slate-600">Tipo</th>
                            <th className="p-2.5 font-semibold text-slate-600">OS</th>
                            <th className="p-2.5 font-semibold text-slate-600">Cliente</th>
                            <th className="p-2.5 font-semibold text-slate-600">Loja</th>
                            <th className="p-2.5 font-semibold text-slate-600">Pagamento</th>
                            <th className="p-2.5 font-semibold text-slate-600 text-right">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.detalhes.map((d, i) => (
                            <tr key={d.id || `${d.hora}-${i}`} className={`border-b border-slate-50 hover:bg-slate-50 ${d.tipo === 'quitacao' ? 'bg-purple-50' : ''}`}>
                              <td className="p-2.5 text-slate-600">{d.hora}</td>
                              <td className="p-2.5">
                                {d.tipo === 'quitacao' ? (
                                  <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                                    Quitação
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                                    Venda
                                  </span>
                                )}
                              </td>
                              <td className="p-2.5 text-slate-600">
                                {d.os || "-"}
                                {d.tipo === 'quitacao' && d.data_venda_original && (
                                  <span className="block text-xs text-purple-500">
                                    Venda: {new Date(d.data_venda_original + "T12:00:00").toLocaleDateString("pt-BR")}
                                  </span>
                                )}
                              </td>
                              <td className="p-2.5 text-slate-900 font-medium">{d.cliente || "-"}</td>
                              <td className="p-2.5 text-slate-600">{d.loja || "-"}</td>
                              <td className="p-2.5 text-slate-600">
                                {d.forma_pagamento || "-"}
                              </td>
                              <td className="p-2.5 text-right font-semibold text-slate-900">
                                {fmt(d.valor)}
                                {d.falta_pagamento && (
                                  <span className="block text-xs text-amber-600 font-normal">Pend: {d.falta_pagamento}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-50 font-bold border-t-2 border-slate-200">
                            <td colSpan="6" className="p-2.5 text-right">TOTAL</td>
                            <td className="p-2.5 text-right">{fmt(dados.stats.total_vendas)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8 text-sm">Nenhuma venda registrada nesta data</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </>
      ) : (
        /* ========== MODO MENSAL (CONFERÊNCIA DE CAIXA) ========== */
        <>
          {/* Filtros Mensais */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs font-medium">Mês</Label>
              <Input 
                type="month" 
                value={mesSelecionado} 
                onChange={(e) => setMesSelecionado(e.target.value)}
                className="w-44" 
                data-testid="input-mes-conferencia" 
              />
            </div>
            <LojaSelector lojaSelecionada={lojaSelecionada} onLojaChange={setLojaSelecionada} />
            <div className="space-y-1">
              <Label className="text-xs font-medium">Forma de Pagamento</Label>
              <Select value={formaPagamentoFiltro} onValueChange={setFormaPagamentoFiltro}>
                <SelectTrigger className="w-40" data-testid="select-forma-pagamento">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMAS_PAGAMENTO.map(forma => (
                    <SelectItem key={forma} value={forma}>{forma}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={carregarDadosMensais} size="sm" data-testid="btn-atualizar-mensal">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Atualizar
            </Button>
          </div>

          {loadingMensal ? (
            <LoadingSpinner message="Carregando conferência mensal..." />
          ) : dadosMensais ? (
            <>
              {/* Resumo do Mês */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-blue-100 text-sm">Total do Mês</p>
                    <p className="text-2xl font-bold mt-1">{fmt(dadosMensais.total_mes)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-slate-500 text-sm">Total de Vendas</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{dadosMensais.total_vendas}</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-slate-500 text-sm">Dias com Vendas</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{dadosMensais.dias_com_vendas}</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border shadow-sm">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-slate-500 text-sm">Ticket Médio</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(dadosMensais.ticket_medio)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Listagem por Dia */}
              <Card className="bg-white border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <List className="w-4 h-4" /> Conferência por Dia
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Clique em um dia para ver os detalhes das vendas
                    {formaPagamentoFiltro !== "Todas" && (
                      <span className="ml-2 text-blue-600">• Filtrado por: {formaPagamentoFiltro}</span>
                    )}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dadosMensais.dias?.length > 0 ? (
                      dadosMensais.dias.map((dia) => (
                        <div key={dia.data} className="border border-slate-200 rounded-lg overflow-hidden">
                          {/* Header do Dia */}
                          <button
                            onClick={() => toggleDiaExpandido(dia.data)}
                            className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                            data-testid={`btn-dia-${dia.data}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-bold text-slate-700 bg-white px-2.5 py-1 rounded border border-slate-200">
                                {new Date(dia.data + "T12:00:00").toLocaleDateString("pt-BR", { weekday: 'short', day: '2-digit', month: '2-digit' })}
                              </span>
                              <span className="text-sm text-slate-600">
                                {dia.quantidade} {dia.quantidade === 1 ? 'venda' : 'vendas'}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-slate-900">{fmt(dia.total)}</span>
                              {diasExpandidos[dia.data] ? (
                                <ChevronUp className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                          </button>
                          
                          {/* Detalhes do Dia (expandível) */}
                          {diasExpandidos[dia.data] && dia.vendas?.length > 0 && (
                            <div className="border-t border-slate-200">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-100 text-left">
                                    <th className="p-2 text-xs font-semibold text-slate-500">Hora</th>
                                    <th className="p-2 text-xs font-semibold text-slate-500">OS</th>
                                    <th className="p-2 text-xs font-semibold text-slate-500">Cliente</th>
                                    <th className="p-2 text-xs font-semibold text-slate-500">Pagamento</th>
                                    <th className="p-2 text-xs font-semibold text-slate-500 text-right">Valor</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dia.vendas.map((v, j) => (
                                    <tr key={v.os || `${v.hora}-${j}`} className="border-b border-slate-50 hover:bg-slate-50">
                                      <td className="p-2 text-slate-600">{v.hora}</td>
                                      <td className="p-2 text-slate-600">{v.os || "-"}</td>
                                      <td className="p-2 text-slate-900 font-medium">{v.cliente || "-"}</td>
                                      <td className="p-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${COR_FORMA_PGTO[v.forma_pagamento] || "bg-slate-100 text-slate-700"}`}>
                                          {v.forma_pagamento || "-"}
                                        </span>
                                      </td>
                                      <td className="p-2 text-right font-semibold text-slate-900">{fmt(v.valor)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-8 text-sm">
                        Nenhuma venda encontrada para este período
                        {formaPagamentoFiltro !== "Todas" && ` com a forma de pagamento "${formaPagamentoFiltro}"`}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ConferenciaCaixa;
